import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Props {
  onClose: () => void;
  workerId: string;
  workerName: string;
}

export function PhotoModal({ onClose, workerId, workerName }: Props) {
  const qc = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [pendingStream, setPendingStream] = useState<MediaStream | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { stopCamera(); onClose(); } }
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); stopCamera(); };
  }, []);

  useEffect(() => {
    if (cameraOn && pendingStream && videoRef.current) {
      videoRef.current.srcObject = pendingStream;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOn, pendingStream]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      setPendingStream(stream);
      setCameraOn(true);
    } catch (err: any) {
      toast.error('Camera error', err?.message ?? 'Permission denied or no camera');
    }
  };

  const stopCamera = () => {
    const stream = (videoRef.current?.srcObject as MediaStream | null) ?? pendingStream;
    stream?.getTracks().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setPendingStream(null);
    setCameraOn(false);
  };

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')!.drawImage(v, 0, 0);
    c.toBlob((b) => {
      if (b) {
        const f = new File([b], `${workerName.replace(/\s+/g, '_')}.jpg`, { type: 'image/jpeg' });
        setFile(f);
        setPreviewUrl(URL.createObjectURL(b));
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  const onFile = (f: File) => {
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('no file');
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post<{ url: string }>('/uploads/photo', fd);
      return api.patch(`/workers/${workerId}`, { photo: up.data.url });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers'] });
      toast.success('Photo saved', workerName);
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="photo-mo-title">📷 Worker Photo</div>
        <div className="modal-sub" id="photo-mo-sub">Take a photo or upload an image — {workerName}</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <button className="btn btn-primary btn-sm" onClick={startCamera}>📷 Use Camera</button>
          <button className="btn btn-ghost btn-sm" onClick={() => document.getElementById('photo-file-inp')?.click()}>📂 Upload File</button>
          <input type="file" id="photo-file-inp" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
        </div>
        {cameraOn ? (
          <div id="photo-camera-wrap" style={{ background: '#000', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
            <video ref={videoRef} id="photo-video" style={{ width: '100%', borderRadius: 10 }} autoPlay playsInline />
            <div style={{ padding: 10, display: 'flex', justifyContent: 'center', gap: 10 }}>
              <button className="btn btn-primary btn-sm" onClick={capture}>📸 Capture</button>
              <button className="btn btn-ghost btn-sm" onClick={stopCamera}>Cancel</button>
            </div>
          </div>
        ) : null}
        <canvas ref={canvasRef} id="photo-canvas" style={{ display: 'none' }} />
        {previewUrl ? (
          <div id="photo-preview" style={{ marginBottom: 12, textAlign: 'center' }}>
            <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 10 }} />
          </div>
        ) : null}
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {file ? (
            <button className="btn btn-primary" id="photo-save-btn" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : '✓ Save Photo'}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
